/** Pure view-model helpers for the class Documents tab (no React, no API). */

export const isPublished = (item) => item.enabled !== false;

/** What kind of file an item is: pdf, doc, xls, img, vid, link, note or file. */
export function getKind(item) {
  if (item.contentType === "LINK") return "link";
  if (item.contentType === "ANNOUNCEMENT") return "note";
  if (item.contentType === "SKILL_SHEET") return "pdf";
  if (item.contentType === "VIDEO") return "vid";
  const name = (item.fileName || "").toLowerCase();
  if (name.endsWith(".pdf")) return "pdf";
  if (/\.(docx?|pptx?)$/.test(name)) return "doc";
  if (/\.xlsx?$/.test(name)) return "xls";
  if (/\.(jpe?g|png|gif|webp)$/.test(name)) return "img";
  if (/\.(mp4|mov|avi|webm)$/.test(name)) return "vid";
  return "file";
}

/** Short label for the file type: "PDF", "DOCX", "URL", "NOTE"… */
export function getExt(item) {
  if (item.contentType === "LINK") return "URL";
  if (item.contentType === "ANNOUNCEMENT") return "NOTE";
  if (item.contentType === "SKILL_SHEET") return "PDF";
  const name = (item.fileName || "").toLowerCase();
  const dot = name.lastIndexOf(".");
  return dot !== -1 ? name.slice(dot + 1).toUpperCase().slice(0, 4) : "FILE";
}

export function formatSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** "today", "yesterday", "8d ago", "3mo ago", "2y ago"; "—" when the date is missing or invalid. */
export function formatRelativeTime(dateStr, now = Date.now()) {
  if (!dateStr) return "—";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.max(0, Math.floor((now - then) / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** The tile drawn at the start of a row: a colour class and either mono text or an icon name. */
const TILES = {
  pdf: { cls: "t-pdf", text: "PDF" },
  doc: { cls: "t-doc", text: "DOC" },
  xls: { cls: "t-skill", text: "XLS" },
  img: { cls: "t-video", text: "IMG" },
  vid: { cls: "t-video", icon: "video" },
  link: { cls: "t-link", icon: "link" },
  note: { cls: "t-test", text: "NOTE" },
  file: { cls: "t-att", text: "FILE" },
};
export const tileFor = (kind) => TILES[kind] || TILES.file;

/** "nfpa.org" from "https://www.nfpa.org/codes"; null when it is not a URL. */
export function linkHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function describe(item, kind, ext) {
  if (kind === "link") {
    const host = linkHost(item.url);
    return host ? `Link · ${host}` : "External link";
  }
  if (kind === "note") return "Announcement";
  const size = formatSize(item.fileSize);
  return size ? `${ext} · ${size}` : ext;
}

/** Everything a row shows for a document stored on the class or a session. */
export function buildDocRow(item, now) {
  const kind = getKind(item);
  const ext = getExt(item);
  const external = item.contentType === "LINK" || (Boolean(item.url) && !item.hasFile);
  return {
    source: "content",
    id: item.id,
    title: item.title,
    description: item.description || "",
    tile: tileFor(kind),
    info: describe(item, kind, ext),
    required: Boolean(item.required),
    featured: Boolean(item.featured),
    instructorViewOnly: Boolean(item.instructorViewOnly),
    published: isPublished(item),
    inLearningPlan: Boolean(item.inLearningPlan),
    fromBank: false,
    primary: external ? "open" : item.hasFile ? "download" : null,
    updated: formatRelativeTime(item.updatedAt, now),
    perStudentLinks: item.contentType === "EXTERNAL_LINK" && item.linkScope === "PER_STUDENT",
  };
}

export const bankTitle = (a) => a.documentTitle || a.document?.title || a.title || `Bank Item #${a.documentId}`;
export const bankDescription = (a) => a.documentDescription || a.document?.description || a.description || "";

/** Everything a row shows for a Knowledge Bank document assigned to the class. */
export function buildBankRow(a) {
  const doc = a.document || {};
  const contentType = a.documentContentType || doc.contentType;
  const item = { contentType, fileName: doc.fileName, fileSize: doc.fileSize, url: doc.url };
  const kind = getKind(item);
  return {
    source: "bank",
    id: a.id,
    title: bankTitle(a),
    description: bankDescription(a),
    tile: tileFor(kind),
    info: contentType ? describe(item, kind, getExt(item)) : "Knowledge Bank",
    required: Boolean(a.required),
    featured: Boolean(a.featured),
    published: Boolean(a.enabled),
    inLearningPlan: Boolean(a.inLearningPlan),
    fromBank: true,
    primary: null,
    updated: null,
    perStudentLinks: false,
  };
}

const matchesFilter = (filter, { required, published }) => {
  if (filter === "required") return required;
  if (filter === "published") return published;
  if (filter === "draft") return !published;
  if (filter === "archived") return false; // archiving is not supported by the backend yet
  return true;
};

/** Content items matching the status filter and search text, in display order. Returns a new array. */
export function filterItems(items, filter = "all", query = "") {
  const q = query.trim().toLowerCase();
  return (items || [])
    .filter((f) => matchesFilter(filter, { required: Boolean(f.required), published: isPublished(f) }))
    .filter((f) => !q
      || f.title?.toLowerCase().includes(q)
      || f.description?.toLowerCase().includes(q)
      || f.contentType?.toLowerCase().includes(q)
      || f.tags?.toLowerCase().includes(q))
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

/** Bank assignments matching the status filter and search text. */
export function filterBank(list, filter = "all", query = "") {
  const q = query.trim().toLowerCase();
  return (list || [])
    .filter((a) => matchesFilter(filter, { required: Boolean(a.required), published: Boolean(a.enabled) }))
    .filter((a) => !q || [bankTitle(a), bankDescription(a)].some((t) => t.toLowerCase().includes(q)));
}

/** Filter-button counts across class-level, session and bank documents. */
export function countFilters(contentLists, bank) {
  const rows = [
    ...contentLists.flat().map((f) => ({ required: Boolean(f.required), published: isPublished(f) })),
    ...(bank || []).map((a) => ({ required: Boolean(a.required), published: Boolean(a.enabled) })),
  ];
  return {
    all: rows.length,
    published: rows.filter((r) => r.published).length,
    draft: rows.filter((r) => !r.published).length,
    required: rows.filter((r) => r.required).length,
  };
}
